import { buildMasterVariantDisplayName } from '../../helpers/buildMasterVariantDisplayName';
import SYNC_FLOW_MEDIA_STEPS from '../constants/syncFlowMediaSteps';
import { showPublishingScopeSetupDialog } from '../PublishingScopeSetupDialog';
import { showProductMediaSelectionDialog } from '../ProductMediaSelectionDialog';
import { showVariantMediaAssignmentDialog } from '../VariantMediaAssignmentDialog';
import {
  createInitialFlowState,
  filterMediaStepsByAvailableAssets,
  getMediaSteps,
  mergeFlowState,
  shouldSkipMediaSteps,
} from './syncFlowOrchestrator';
import {
  buildInitialProductMediaByChannel,
  buildInitialVariantMediaByChannel,
  getAttachedAssetsForVariant,
  getProductMediaAssets,
  getSelectedVariantIdsForSync,
  retainMediaByChannelForUnchangedActions,
} from './syncFlowMediaHelpers';

const needsUnmappedGuard = (flowState) => Boolean(flowState.publishingScope?.text);

const buildValidateBeforeFinish = ({ flowState, validateUnmappedFields }) => {
  if (!needsUnmappedGuard(flowState) || !validateUnmappedFields) {
    return undefined;
  }

  return (onUnmappedDialogOpen) => validateUnmappedFields({
    channelIds: flowState.selectedChannels,
    onUnmappedDialogOpen,
  });
};

const buildVariantOptions = (product, masterVariantIds, productsState) => {
  const variantIds = getSelectedVariantIdsForSync(product, masterVariantIds);

  return variantIds
    .map((variantId) => {
      const variant = product?.variants?.find(({ _id }) => _id === variantId);
      const attachedAssets = getAttachedAssetsForVariant(product, variantId, productsState);

      return {
        id: variantId,
        label: buildMasterVariantDisplayName({
          productTitle: product?.title || '',
          availableOptions: product?.availableOptions || [],
          variantOptions: variant?.options || {},
        }) || variant?.title || variantId,
        attachedAssets,
      };
    })
    .filter(({ attachedAssets }) => attachedAssets.length > 0);
};

const buildInitialSetupState = (flowState) => {
  if (!flowState?.selectedChannels?.length) {
    return undefined;
  }

  return {
    selectedChannels: flowState.selectedChannels,
    channelActionsByChannelId: flowState.channelActionsByChannelId,
    publishingScope: flowState.publishingScope,
    overrideExisting: flowState.overrideExisting,
  };
};

const mergeMediaFlowState = ({
  flowState,
  product,
  productsState,
  productListings,
}) => mergeFlowState(flowState, {
  productMediaByChannel: buildInitialProductMediaByChannel({
    selectedChannels: flowState.selectedChannels,
    product,
    productsState,
    productListings,
    channelActionsByChannelId: flowState.channelActionsByChannelId,
    existingProductMediaByChannel: flowState.productMediaByChannel,
  }),
  variantMediaByChannel: buildInitialVariantMediaByChannel({
    selectedChannels: flowState.selectedChannels,
    product,
    productsState,
    productListings,
    masterVariantIds: flowState.masterVariantIds,
    channelActionsByChannelId: flowState.channelActionsByChannelId,
    existingVariantMediaByChannel: flowState.variantMediaByChannel,
  }),
});

const runMediaSteps = ({
  flowState,
  product,
  productsState,
  channelOptions,
  stepIndex,
  mediaSteps,
  onCancel,
  onComplete,
  validateUnmappedFields,
  onValidationAbort,
  openSetupDialog,
}) => {
  const currentStep = mediaSteps[stepIndex];

  if (!currentStep) {
    onComplete(flowState);
    return;
  }

  const isLastStep = stepIndex === mediaSteps.length - 1;
  const validateBeforeFinish = isLastStep
    ? buildValidateBeforeFinish({ flowState, validateUnmappedFields })
    : undefined;
  const previousStep = mediaSteps[stepIndex - 1];

  if (currentStep === SYNC_FLOW_MEDIA_STEPS.PRODUCT_MEDIA) {
    showProductMediaSelectionDialog({
      channelOptions,
      selectedChannels: flowState.selectedChannels,
      assets: getProductMediaAssets(product, productsState),
      productMediaByChannel: flowState.productMediaByChannel,
      validateBeforeFinish,
      onCancel,
      onValidationAbort,
      onBack: (productMediaByChannel) => {
        openSetupDialog(mergeFlowState(flowState, { productMediaByChannel }));
      },
      onConfirm: (productMediaByChannel) => {
        runMediaSteps({
          flowState: mergeFlowState(flowState, { productMediaByChannel }),
          product,
          productsState,
          channelOptions,
          stepIndex: stepIndex + 1,
          mediaSteps,
          onCancel,
          onComplete,
          validateUnmappedFields,
          onValidationAbort,
          openSetupDialog,
        });
      },
    });
    return;
  }

  if (currentStep === SYNC_FLOW_MEDIA_STEPS.VARIANT_MEDIA) {
    showVariantMediaAssignmentDialog({
      channelOptions,
      selectedChannels: flowState.selectedChannels,
      variantOptions: buildVariantOptions(product, flowState.masterVariantIds, productsState),
      variantMediaByChannel: flowState.variantMediaByChannel,
      validateBeforeFinish,
      onCancel,
      onValidationAbort,
      onBack: (variantMediaByChannel) => {
        const nextFlowState = mergeFlowState(flowState, { variantMediaByChannel });

        if (previousStep === SYNC_FLOW_MEDIA_STEPS.PRODUCT_MEDIA) {
          runMediaSteps({
            flowState: nextFlowState,
            product,
            productsState,
            channelOptions,
            stepIndex: stepIndex - 1,
            mediaSteps,
            onCancel,
            onComplete,
            validateUnmappedFields,
            onValidationAbort,
            openSetupDialog,
          });
          return;
        }

        openSetupDialog(nextFlowState);
      },
      onConfirm: (variantMediaByChannel) => {
        runMediaSteps({
          flowState: mergeFlowState(flowState, {
            variantMediaByChannel,
            variantMediaStepCompleted: true,
          }),
          product,
          productsState,
          channelOptions,
          stepIndex: stepIndex + 1,
          mediaSteps,
          onCancel,
          onComplete,
          validateUnmappedFields,
          onValidationAbort,
          openSetupDialog,
        });
      },
    });
  }
};

const continueAfterScopeSetup = ({
  flowState,
  product,
  productsState,
  productListings,
  channelOptions,
  onCancel,
  onComplete,
  validateUnmappedFields,
  onValidationAbort,
  openSetupDialog,
}) => {
  if (shouldSkipMediaSteps(flowState.publishingScope)) {
    onComplete(flowState);
    return;
  }

  const mediaSteps = filterMediaStepsByAvailableAssets({
    steps: getMediaSteps({
      product,
      isVariantSync: flowState.isVariantSync,
      scope: flowState.publishingScope,
    }),
    product,
    masterVariantIds: flowState.masterVariantIds,
    productsState,
  });

  runMediaSteps({
    flowState: mergeMediaFlowState({
      flowState,
      product,
      productsState,
      productListings,
    }),
    product,
    productsState,
    channelOptions,
    stepIndex: 0,
    mediaSteps,
    onCancel,
    onComplete,
    validateUnmappedFields,
    onValidationAbort,
    openSetupDialog,
  });
};

export const startSyncFlow = ({
  product,
  productId,
  productsState,
  productListings,
  channelOptions,
  masterVariantIds,
  isVariantSync = false,
  shouldShowVariantNote,
  onCancel,
  onComplete,
  validateUnmappedFields,
}) => {
  const flowState = createInitialFlowState({
    productId,
    masterVariantIds,
    isVariantSync,
  });

  const openSetupDialog = (flowStateOverride = null) => {
    const currentFlowState = flowStateOverride ?? flowState;

    showPublishingScopeSetupDialog({
      channelOptions,
      productListings,
      product,
      productsState,
      isVariantSync,
      masterVariantIds,
      shouldShowVariantNote,
      initialSetupState: buildInitialSetupState(currentFlowState),
      onCancel,
      onValidationAbort: () => openSetupDialog(currentFlowState),
      onProceed: async ({
        selectedChannels,
        channelActionsByChannelId,
        publishingScope,
        overrideExisting,
      }, { onUnmappedDialogOpen } = {}) => {
        const activeSelectedChannels = selectedChannels.filter((channelId) => {
          const option = channelOptions.find(({ value }) => value === channelId);
          return option && !option.disconnected;
        });

        if (!activeSelectedChannels.length) {
          return;
        }

        const activeChannelActions = activeSelectedChannels.reduce((acc, channelId) => ({
          ...acc,
          [channelId]: channelActionsByChannelId[channelId],
        }), {});

        const previousChannelActions = currentFlowState.channelActionsByChannelId || {};
        const retainedProductMediaByChannel = retainMediaByChannelForUnchangedActions({
          mediaByChannel: currentFlowState.productMediaByChannel,
          previousActionsByChannelId: previousChannelActions,
          nextActionsByChannelId: activeChannelActions,
          selectedChannels: activeSelectedChannels,
        });
        const retainedVariantMediaByChannel = retainMediaByChannelForUnchangedActions({
          mediaByChannel: currentFlowState.variantMediaByChannel,
          previousActionsByChannelId: previousChannelActions,
          nextActionsByChannelId: activeChannelActions,
          selectedChannels: activeSelectedChannels,
        });

        const nextFlowState = mergeFlowState(currentFlowState, {
          selectedChannels: activeSelectedChannels,
          channelActionsByChannelId: activeChannelActions,
          publishingScope,
          overrideExisting,
          productMediaByChannel: retainedProductMediaByChannel,
          variantMediaByChannel: retainedVariantMediaByChannel,
        });

        if (
          needsUnmappedGuard(nextFlowState)
          && shouldSkipMediaSteps(nextFlowState.publishingScope)
          && validateUnmappedFields
        ) {
          await validateUnmappedFields({
            channelIds: nextFlowState.selectedChannels,
            onUnmappedDialogOpen,
          });
        }

        continueAfterScopeSetup({
          flowState: nextFlowState,
          product,
          productsState,
          productListings,
          channelOptions,
          onCancel,
          onComplete,
          validateUnmappedFields,
          onValidationAbort: () => openSetupDialog(nextFlowState),
          openSetupDialog,
        });
      },
    });
  };

  openSetupDialog();
};

export default startSyncFlow;
